import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, Package, FileText, TrendingUp, TrendingDown, Wallet, AlertCircle, Plug, Cake, MessageCircle, BellRing, PhoneCall, Undo2, CalendarDays, Target, Pencil, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function buildWhatsAppLink(phoneRaw, message) {
  if (!phoneRaw) return null;
  let phone = String(phoneRaw).replace(/[^\d+]/g, "");
  if (phone.startsWith("00")) phone = "+" + phone.slice(2);
  if (!phone.startsWith("+")) {
    if (phone.length === 9) phone = "+34" + phone;
  }
  phone = phone.replace("+", "");
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";

export default function Dashboard() {
  const { user, centers } = useAuth();
  const [centerFilter, setCenterFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [pendingCollections, setPendingCollections] = useState([]);

  useEffect(() => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/reports/summary", { params }).then((r) => setSummary(r.data));
  }, [centerFilter, user]);

  useEffect(() => {
    // Carga cobros pendientes/vencidos para admin y gerente
    if (!user || !["admin", "gerente"].includes(user.role)) return;
    const params = { status: "pending" };
    if (["admin", "center_admin"].includes(user.role) && centerFilter !== "all") params.center_id = centerFilter;
    api
      .get("/financing-collections", { params })
      .then((r) => setPendingCollections(r.data || []))
      .catch(() => setPendingCollections([]));
  }, [centerFilter, user]);

  if (!summary) return <div className="text-[#6E7772]">Cargando…</div>;

  const isAdmin = ["admin", "center_admin"].includes(user?.role);
  const canSeeStock = isAdmin || user?.role === "gerente";

  const cards = isAdmin
    ? [
        {
          label: "Pacientes activos",
          value: summary.patients_count,
          icon: Users,
          tone: "bg-white",
          testid: "kpi-patients",
        },
        {
          label: "Ingresos",
          value: euro(summary.total_income || 0),
          icon: TrendingUp,
          tone: "bg-[#D1E0D7]",
          testid: "kpi-income",
        },
        {
          label: "Gastos",
          value: euro(summary.total_expense || 0),
          icon: TrendingDown,
          tone: "bg-[#F3E5D8]",
          testid: "kpi-expense",
        },
        {
          label: "Balance",
          value: euro(summary.balance || 0),
          icon: Wallet,
          tone: (summary.balance || 0) >= 0 ? "bg-[#EAE6DF]" : "bg-[#E8D1D1]",
          testid: "kpi-balance",
        },
      ]
    : [
        {
          label: "Pacientes activos",
          value: summary.patients_count,
          icon: Users,
          tone: "bg-white",
          testid: "kpi-patients",
        },
        {
          label: "Facturas emitidas",
          value: summary.invoices_count,
          icon: FileText,
          tone: "bg-[#EAE6DF]",
          testid: "kpi-invoices",
        },
        ...(canSeeStock
          ? [{
              label: "Stock bajo",
              value: summary.stock_low,
              icon: Package,
              tone: summary.stock_low > 0 ? "bg-[#F3E5D8]" : "bg-white",
              testid: "kpi-stock-low",
            }]
          : []),
      ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Bienvenido
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Hola, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#6E7772] mt-1 text-sm">
            Visión global de tus centros auditivos.
          </p>
        </div>

        {["admin", "center_admin"].includes(user?.role) && (
          <div className="w-56">
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger data-testid="center-filter" className="bg-white border-[#E5E3DB]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
        {cards.map((c) => (
          <Card
            key={c.label}
            data-testid={c.testid}
            className={`border-[#E5E3DB] shadow-none ${c.tone}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
                    {c.label}
                  </div>
                  <div className="font-heading text-2xl md:text-3xl text-[#2A2F2C] mt-2 tracking-tight">
                    {c.value}
                  </div>
                </div>
                <c.icon strokeWidth={1.5} className="h-5 w-5 text-[#4A5D4E]" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(isAdmin || user?.role === "gerente") && (
        <MonthlySummaryCard centerFilter={centerFilter} user={user} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#E5E3DB] shadow-none lg:col-span-2 bg-white">
          <CardHeader>
            <CardTitle className="font-heading font-medium text-lg">
              Ingresos vs Gastos (últimos meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary.monthly} margin={{ left: 0, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                  <XAxis dataKey="month" stroke="#6E7772" fontSize={12} />
                  <YAxis stroke="#6E7772" fontSize={12} />
                  <Tooltip
                    formatter={(v) => euro(v)}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #E5E3DB",
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="income" name="Ingresos" fill="#4A5D4E" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Gastos" fill="#C07F67" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E3DB] shadow-none bg-white">
          <CardHeader>
            <CardTitle className="font-heading font-medium text-lg">
              Resumen rápido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between border-b border-[#E5E3DB] pb-3">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
                <span className="text-sm text-[#4B5563]">Facturas emitidas</span>
              </div>
              <span className="font-medium" data-testid="invoices-count">
                {summary.invoices_count}
              </span>
            </div>
            {canSeeStock && (
              <div className="flex items-center justify-between border-b border-[#E5E3DB] pb-3">
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-[#C07F67]" strokeWidth={1.5} />
                  <span className="text-sm text-[#4B5563]">Stock bajo</span>
                </div>
                <span className="font-medium" data-testid="stock-low">{summary.stock_low}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
                <span className="text-sm text-[#4B5563]">Pacientes registrados</span>
              </div>
              <span className="font-medium">{summary.patients_count}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cobros pendientes de financiación por el centro (solo admin/gerente) */}
      {["admin", "gerente"].includes(user?.role) && pendingCollections.length > 0 && (
        <Card className="border-[#E5D8C0] bg-[#FBF7EF]" data-testid="pending-collections-card">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-[#C07F67]" />
                Cobros pendientes (financiación interna)
              </CardTitle>
              <p className="text-xs text-[#6E7772] mt-1">
                Cuotas mensuales que aún no se han marcado como cobradas. Vencidas en rojo.
              </p>
            </div>
            <span className="text-3xl font-heading font-semibold text-[#7C5A2A]" data-testid="pending-collections-count">
              {pendingCollections.length}
            </span>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {pendingCollections.slice(0, 12).map((c) => {
                const today = new Date().toISOString().slice(0, 10);
                const overdue = c.due_date < today;
                const waMsg = `Hola ${(c.patient_name || "").split(" ")[0]}, te recordamos que tu cuota de ${euro(c.amount)} (factura ${c.invoice_number}) vence el ${c.due_date}. Un saludo.`;
                const waLink = buildWhatsAppLink(c.patient_phone, waMsg);
                return (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded border border-[#E5D8C0] bg-white px-3 py-2 hover:bg-[#FAF9F6] transition gap-2"
                    data-testid={`dash-collection-${c.id}`}
                  >
                    <Link to={`/pacientes/${c.patient_id}`} className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-[#2A2F2C] hover:underline truncate">
                        {c.patient_name || "Paciente"} — {euro(c.amount)}
                      </span>
                      <span className={`text-xs ${overdue ? "text-[#C0392B]" : "text-[#6E7772]"}`}>
                        Vence {c.due_date} · Cuota {c.installment_no}/{c.total_installments}
                        {overdue && " · VENCIDA"}
                      </span>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      {waLink && (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#4A5D4E] border border-[#D1E0D7] bg-[#F2F7F4] hover:bg-[#E4EFE8] rounded px-2 py-1 transition-colors"
                          data-testid={`dash-collection-remind-${c.id}`}
                          title="Recordar la cuota al paciente por WhatsApp"
                        >
                          <MessageCircle className="h-3.5 w-3.5" /> Recordar
                        </a>
                      )}
                      <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider hidden md:inline">
                        Fact. {c.invoice_number}
                      </span>
                    </div>
                  </div>
                );
              })}
              {pendingCollections.length > 12 && (
                <div className="text-xs text-[#6E7772] text-center pt-1">
                  Y {pendingCollections.length - 12} cuotas más…
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <BirthdaysCard centerFilter={centerFilter} user={user} centers={centers} />

      <AnnualReviewsCard centerFilter={centerFilter} user={user} centers={centers} />

      <QuotesFollowUpCard centerFilter={centerFilter} user={user} />

      <WarrantyExpiryCard centerFilter={centerFilter} user={user} centers={centers} />

      {isAdmin && <IntegrationsStatusCard />}
    </div>
  );
}

function QuotesFollowUpCard({ centerFilter, user }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/quotes/follow-ups/pending", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [centerFilter, user]);
  if (!data) return null;
  const list = data.quotes || [];
  const badge = (q) => {
    if (q.kind === "aceptado")
      return <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#D1E0D7] text-[#1F2A24] rounded-full px-2 py-0.5">Aceptado — facturar</span>;
    if (q.kind === "caducado")
      return <span className="text-[10px] font-semibold uppercase tracking-wider bg-red-100 text-red-700 rounded-full px-2 py-0.5">Caducado hace {Math.abs(q.days_left)} día(s)</span>;
    return (
      <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#FBF3E4] text-[#7C5A2A] rounded-full px-2 py-0.5">
        {q.days_left === 0 ? "Caduca HOY" : `Caduca en ${q.days_left} día(s)`}
      </span>
    );
  };
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="quotes-followup-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} /> Presupuestos por facturar o a punto de caducar
            {data.count > 0 && (
              <span className="text-xs bg-[#FBF3E4] text-[#7C5A2A] rounded-full px-2 py-0.5">{data.count}</span>
            )}
          </CardTitle>
          {data.count > 0 && (
            <Link to="/presupuestos" className="text-xs text-[#4A5D4E] hover:underline" data-testid="quotes-followup-see-all">
              Ver todos
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-[#6E7772]" data-testid="quotes-followup-empty">
            No hay presupuestos aceptados pendientes de facturar ni presupuestos a punto de caducar.
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {list.slice(0, 10).map((q) => {
              const waMsg = `Hola ${q.patient_name?.split(" ")[0] || ""}, te recordamos que tu presupuesto ${q.quote_number} de ${euro(q.total)} ${q.valid_until ? `es válido hasta el ${q.valid_until}` : "sigue disponible"}. ¿Quieres que te llamemos para resolver cualquier duda?`;
              const waLink = buildWhatsAppLink(q.patient_phone, waMsg);
              return (
                <div
                  key={q.id}
                  className="flex items-center justify-between rounded border border-[#E5E3DB] bg-white px-3 py-2 gap-2"
                  data-testid={`quotes-followup-row-${q.id}`}
                >
                  <Link to={`/pacientes/${q.patient_id}`} className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-[#2A2F2C] hover:underline truncate">
                      {q.quote_number} · {q.patient_name}
                    </span>
                    <span className="text-xs text-[#6E7772] truncate">
                      {euro(q.total)}{q.valid_until ? ` · válido hasta ${q.valid_until}` : ""}
                    </span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {badge(q)}
                    {q.kind !== "aceptado" && waLink && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                        title="Recordar al paciente por WhatsApp"
                        className="text-[#25D366] hover:opacity-80"
                        data-testid={`quotes-followup-wa-${q.id}`}
                      >
                        <MessageCircle className="h-4 w-4" strokeWidth={2} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MonthlySummaryCard({ centerFilter, user }) {
  const [data, setData] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const canEditGoal = ["admin", "center_admin"].includes(user?.role);
  const load = () => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/stats/monthly-summary", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };
  useEffect(() => {
    setEditingGoal(false);
    load();
    // eslint-disable-next-line
  }, [centerFilter, user]);
  if (!data) return null;
  const saveGoal = async () => {
    const amount = parseFloat(String(goalInput).replace(",", ".")) || 0;
    setSavingGoal(true);
    try {
      await api.put("/stats/sales-goal", { scope: data.goal?.scope || "all", amount });
      toast.success(amount > 0 ? "Objetivo del mes guardado" : "Objetivo eliminado");
      setEditingGoal(false);
      load();
    } catch {
      toast.error("No se pudo guardar el objetivo");
    } finally {
      setSavingGoal(false);
    }
  };
  const monthName = (iso) => {
    try {
      const n = new Date(iso + "T12:00:00").toLocaleDateString("es-ES", { month: "long" });
      return n.charAt(0).toUpperCase() + n.slice(1);
    } catch { return iso; }
  };
  const curLabel = monthName(data.current.from);
  const prevLabel = monthName(data.previous.from);
  const metrics = [
    {
      key: "sales",
      label: "Ventas",
      icon: TrendingUp,
      unit: (n) => `${n} factura${n === 1 ? "" : "s"}`,
      badWhenUp: false,
    },
    {
      key: "collections",
      label: "Cobros de cuotas",
      icon: Wallet,
      unit: (n) => `${n} cuota${n === 1 ? "" : "s"} cobrada${n === 1 ? "" : "s"}`,
      badWhenUp: false,
    },
    {
      key: "returns",
      label: "Devoluciones",
      icon: Undo2,
      unit: (n) => `${n} devolución${n === 1 ? "" : "es"}`,
      badWhenUp: true,
    },
  ];
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="monthly-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
          Resumen de {curLabel}
          <span className="text-xs font-normal text-[#9CA3AF]">(hasta hoy · comparado con {prevLabel})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {metrics.map((m) => {
            const cur = data.current[m.key];
            const prev = data.previous[m.key];
            const pct = prev.total > 0 ? Math.round(((cur.total - prev.total) / prev.total) * 100) : null;
            const up = cur.total > prev.total;
            const equal = cur.total === prev.total;
            const good = equal ? null : m.badWhenUp ? !up : up;
            const Arrow = up ? TrendingUp : TrendingDown;
            return (
              <div key={m.key} className="rounded border border-[#E5E3DB] p-4" data-testid={`month-metric-${m.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">{m.label}</span>
                  <m.icon className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.5} />
                </div>
                <div className="font-heading text-2xl text-[#2A2F2C] mt-2 tracking-tight">{euro(cur.total)}</div>
                <div className="text-xs text-[#6E7772] mt-0.5">{m.unit(cur.count)}</div>
                <div className="flex items-center gap-1.5 mt-2 text-xs">
                  {!equal && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${good ? "bg-[#D1E0D7] text-[#2A2F2C]" : "bg-[#E8D1D1] text-[#8A2E2E]"}`}
                      data-testid={`month-delta-${m.key}`}
                    >
                      <Arrow className="h-3 w-3" />
                      {pct == null ? (up ? "Nuevo" : "—") : `${pct > 0 ? "+" : ""}${pct}%`}
                    </span>
                  )}
                  <span className="text-[#9CA3AF]">
                    {prevLabel}: {euro(prev.total)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {(() => {
          const goalAmount = data.goal?.amount || 0;
          if (!goalAmount && !canEditGoal) return null;
          const sales = data.current.sales.total;
          const now = new Date();
          const daysLeft = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();
          const pct = goalAmount > 0 ? Math.min(100, Math.round((sales / goalAmount) * 100)) : 0;
          const achieved = goalAmount > 0 && sales >= goalAmount;
          const showForm = canEditGoal && (editingGoal || !goalAmount);
          return (
            <div className="mt-4 rounded border border-[#E5E3DB] p-4" data-testid="sales-goal-block">
              {showForm ? (
                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772] flex items-center gap-1.5 mb-1">
                      <Target className="h-3.5 w-3.5 text-[#4A5D4E]" /> Objetivo de ventas de {curLabel} (€)
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="Ej. 10000"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      className="w-44 bg-white"
                      data-testid="sales-goal-input"
                    />
                  </div>
                  <Button
                    onClick={saveGoal}
                    disabled={savingGoal}
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                    data-testid="sales-goal-save"
                  >
                    {savingGoal ? "Guardando…" : "Guardar objetivo"}
                  </Button>
                  {editingGoal && (
                    <Button variant="ghost" onClick={() => setEditingGoal(false)} data-testid="sales-goal-cancel">
                      Cancelar
                    </Button>
                  )}
                </div>
              ) : goalAmount > 0 ? (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772] flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5 text-[#4A5D4E]" /> Objetivo de ventas de {curLabel}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#2A2F2C]" data-testid="sales-goal-progress-text">
                        {euro(sales)} / {euro(goalAmount)}
                      </span>
                      {canEditGoal && (
                        <button
                          onClick={() => {
                            setGoalInput(String(goalAmount));
                            setEditingGoal(true);
                          }}
                          className="h-6 w-6 inline-flex items-center justify-center rounded text-[#7C5A2A] hover:bg-[#F3E7CE] transition-colors"
                          data-testid="sales-goal-edit"
                          title="Cambiar el objetivo del mes"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 h-3 rounded-full bg-[#EAE6DF] overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${achieved ? "bg-[#4A5D4E]" : "bg-[#C9A961]"}`}
                      style={{ width: `${pct}%` }}
                      data-testid="sales-goal-bar"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1.5">
                    <span className={achieved ? "text-[#4A5D4E] font-semibold" : "text-[#6E7772]"}>{pct}% conseguido</span>
                    <span className={achieved ? "text-[#4A5D4E] font-semibold" : "text-[#6E7772]"} data-testid="sales-goal-remaining">
                      {achieved
                        ? "¡Objetivo conseguido!"
                        : `Faltan ${euro(goalAmount - sales)} · ${daysLeft === 0 ? "último día del mes" : `quedan ${daysLeft} día${daysLeft === 1 ? "" : "s"}`}`}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

function AnnualReviewsCard({ centerFilter, user, centers }) {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState("");
  const load = () => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/patients/follow-ups/annual-review", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };
  useEffect(load, [centerFilter, user]);
  if (!data) return null;
  const centerName = (cid) => (centers || []).find((c) => c.id === cid)?.name || "Amplisoon";
  const monthsSince = (iso) => {
    if (!iso) return null;
    const d = new Date(iso + "T12:00:00");
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  };
  const markContacted = async (p) => {
    setMarking(p.patient_id);
    try {
      await api.post(`/patients/${p.patient_id}/contacts`, {
        channel: "call",
        result: "done",
        notes: "Revisión anual — contactado desde el Panel",
      });
      toast.success(`${p.name} marcado como contactado`);
      load();
    } catch (err) {
      toast.error("No se pudo registrar el contacto");
    } finally {
      setMarking("");
    }
  };
  const list = data.patients || [];
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="annual-reviews-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
            <BellRing className="h-5 w-5 text-[#7C5A2A]" strokeWidth={1.5} /> Revisiones anuales pendientes
            {data.count > 0 && (
              <span className="text-xs bg-[#FBF3E4] text-[#7C5A2A] rounded-full px-2 py-0.5">{data.count}</span>
            )}
          </CardTitle>
          {data.count > 0 && (
            <Link to="/estadisticas-pacientes" className="text-xs text-[#4A5D4E] hover:underline" data-testid="annual-reviews-see-all">
              Ver todas
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-[#6E7772]" data-testid="annual-reviews-empty">
            Ningún paciente tiene la revisión anual pendiente ahora mismo. Aparecerán aquí cuando su audífono cumpla ~1 año desde la adaptación (y sin contacto en los últimos 4 meses).
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {list.slice(0, 10).map((p) => {
              const m = monthsSince(p.last_fitting_date);
              const fullName = `${p.name || ""} ${p.surname || ""}`.trim();
              const waMsg = `Hola ${p.name || ""}, somos ${centerName(p.center_id)}. Tu audífono ${p.hearing_aid || ""} ya cumple un año: ¿te va bien que te citemos para la revisión anual gratuita?`;
              const waLink = buildWhatsAppLink(p.phone, waMsg);
              return (
                <div
                  key={p.patient_id}
                  className="flex items-center justify-between rounded border border-[#E5E3DB] bg-white px-3 py-2 gap-2"
                  data-testid={`annual-review-row-${p.patient_id}`}
                >
                  <Link to={`/pacientes/${p.patient_id}`} className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-[#2A2F2C] hover:underline truncate">{fullName}</span>
                    <span className="text-xs text-[#6E7772] truncate">
                      {p.hearing_aid || "Audífono"} · adaptado {m != null ? `hace ${m} meses` : p.last_fitting_date}
                      {p.phone ? ` · ${p.phone}` : ""}
                    </span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {waLink && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#4A5D4E] border border-[#D1E0D7] bg-[#F2F7F4] hover:bg-[#E4EFE8] rounded px-2 py-1 transition-colors"
                        data-testid={`annual-review-wa-${p.patient_id}`}
                        title="Proponer revisión por WhatsApp"
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    )}
                    <button
                      onClick={() => markContacted(p)}
                      disabled={marking === p.patient_id}
                      className="inline-flex items-center gap-1 text-xs text-[#7C5A2A] border border-[#E5D8C0] bg-[#FBF3E4] hover:bg-[#F3E7CE] rounded px-2 py-1 transition-colors disabled:opacity-50"
                      data-testid={`annual-review-done-${p.patient_id}`}
                      title="Registrar que ya has llamado a este paciente (se ocultará 4 meses)"
                    >
                      <PhoneCall className="h-3.5 w-3.5" /> {marking === p.patient_id ? "Guardando…" : "Contactado"}
                    </button>
                  </div>
                </div>
              );
            })}
            {list.length > 10 && (
              <p className="text-xs text-[#9CA3AF]">y {list.length - 10} más — ver todas en Estadísticas CRM.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BirthdaysCard({ centerFilter, user, centers }) {
  const [birthdays, setBirthdays] = useState(null);
  useEffect(() => {
    const params = {};
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/patients/birthdays", { params })
      .then((r) => setBirthdays(r.data || []))
      .catch(() => setBirthdays([]));
  }, [centerFilter, user]);
  if (!birthdays) return null;
  const centerName = (cid) => (centers || []).find((c) => c.id === cid)?.name || "Amplisoon";
  const fmtDay = (iso) => {
    try {
      return new Date(iso + "T12:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
    } catch { return iso; }
  };
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="birthdays-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <Cake className="h-5 w-5 text-[#C07F67]" strokeWidth={1.5} /> Cumpleaños esta semana
        </CardTitle>
      </CardHeader>
      <CardContent>
        {birthdays.length === 0 ? (
          <p className="text-sm text-[#6E7772]" data-testid="birthdays-empty">
            Ningún paciente cumple años en los próximos 7 días.
          </p>
        ) : (
          <div className="space-y-2">
            {birthdays.map((b) => {
              const waMsg = `¡Feliz cumpleaños, ${b.name.split(" ")[0]}! 🎂 Todo el equipo de ${centerName(b.center_id)} te desea un día estupendo.`;
              const waLink = buildWhatsAppLink(b.phone, waMsg);
              return (
                <div
                  key={b.patient_id}
                  className={`flex items-center justify-between rounded border px-3 py-2 ${b.is_today ? "border-[#E5C9A0] bg-[#FBF7EF]" : "border-[#E5E3DB] bg-white"}`}
                  data-testid={`birthday-row-${b.patient_id}`}
                >
                  <div className="flex flex-col">
                    <Link to={`/pacientes/${b.patient_id}`} className="text-sm font-medium text-[#2A2F2C] hover:underline">
                      {b.name}
                    </Link>
                    <span className="text-xs text-[#6E7772]">
                      Cumple <strong>{b.turns} años</strong>{" "}
                      {b.is_today ? <span className="text-[#C07F67] font-semibold uppercase">¡hoy!</span> : `el ${fmtDay(b.date)}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.phone && (
                      <span className="text-xs text-[#6E7772] hidden sm:inline">{b.phone}</span>
                    )}
                    {waLink && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#4A5D4E] border border-[#D1E0D7] bg-[#F2F7F4] hover:bg-[#E4EFE8] rounded px-2 py-1 transition-colors"
                        data-testid={`birthday-wa-${b.patient_id}`}
                        title="Felicitar por WhatsApp"
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> Felicitar
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WarrantyExpiryCard({ centerFilter, user, centers }) {
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState("");
  const load = () => {
    const params = { days: 60 };
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;
    api.get("/hearing-aids/warranty-expiring", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };
  useEffect(load, [centerFilter, user]);
  if (!data) return null;
  const centerName = (cid) => (centers || []).find((c) => c.id === cid)?.name || "Amplisoon";
  const fmtDate = (iso) => {
    try { return new Date(iso + "T12:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
  };
  const daysTo = (iso) => Math.max(0, Math.ceil((new Date(iso + "T23:59:59") - Date.now()) / 86400000));
  const markContacted = async (p) => {
    setMarking(p.patient_id);
    try {
      await api.post(`/patients/${p.patient_id}/contacts`, {
        channel: "call",
        result: "done",
        notes: "Fin de garantía — contactado desde el Panel",
      });
      toast.success(`${p.name} marcado como contactado`);
      load();
    } catch {
      toast.error("No se pudo registrar el contacto");
    } finally {
      setMarking("");
    }
  };
  const list = data.items || [];
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="warranty-expiry-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#8A2E2E]" strokeWidth={1.5} /> Garantías que terminan pronto
          {data.count > 0 && (
            <span className="text-xs bg-[#E8D1D1] text-[#8A2E2E] rounded-full px-2 py-0.5">{data.count}</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-[#6E7772]" data-testid="warranty-expiry-empty">
            Ninguna garantía termina en los próximos 60 días. Cuando un audífono esté a punto de quedarse sin garantía, aparecerá aquí para ofrecer la ampliación.
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {list.slice(0, 10).map((p) => {
              const fullName = `${p.name || ""} ${p.surname || ""}`.trim();
              const d = daysTo(p.warranty_until);
              const waMsg = `Hola ${p.name || ""}, somos ${centerName(p.center_id)}. La garantía de tu audífono ${p.hearing_aid || ""} termina el ${fmtDate(p.warranty_until)}. ¿Quieres que te informemos de las opciones para ampliarla?`;
              const waLink = buildWhatsAppLink(p.phone, waMsg);
              return (
                <div
                  key={`${p.patient_id}-${p.warranty_until}`}
                  className={`flex items-center justify-between rounded border px-3 py-2 gap-2 ${d <= 15 ? "border-[#E5C1C1] bg-[#FBF1F1]" : "border-[#E5E3DB] bg-white"}`}
                  data-testid={`warranty-row-${p.patient_id}`}
                >
                  <Link to={`/pacientes/${p.patient_id}`} className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-[#2A2F2C] hover:underline truncate">{fullName}</span>
                    <span className="text-xs text-[#6E7772] truncate">
                      {p.hearing_aid || "Audífono"} · garantía hasta el {fmtDate(p.warranty_until)}{" "}
                      <span className={d <= 15 ? "text-[#8A2E2E] font-semibold" : ""}>
                        ({d === 0 ? "último día" : `quedan ${d} día${d === 1 ? "" : "s"}`})
                      </span>
                      {p.phone ? ` · ${p.phone}` : ""}
                    </span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    {waLink && (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#4A5D4E] border border-[#D1E0D7] bg-[#F2F7F4] hover:bg-[#E4EFE8] rounded px-2 py-1 transition-colors"
                        data-testid={`warranty-wa-${p.patient_id}`}
                        title="Ofrecer ampliación de garantía por WhatsApp"
                      >
                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    )}
                    <button
                      onClick={() => markContacted(p)}
                      disabled={marking === p.patient_id}
                      className="inline-flex items-center gap-1 text-xs text-[#7C5A2A] border border-[#E5D8C0] bg-[#FBF3E4] hover:bg-[#F3E7CE] rounded px-2 py-1 transition-colors disabled:opacity-50"
                      data-testid={`warranty-done-${p.patient_id}`}
                      title="Registrar que ya has contactado a este paciente"
                    >
                      <PhoneCall className="h-3.5 w-3.5" /> {marking === p.patient_id ? "Guardando…" : "Contactado"}
                    </button>
                  </div>
                </div>
              );
            })}
            {list.length > 10 && <p className="text-xs text-[#9CA3AF]">y {list.length - 10} más.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IntegrationsStatusCard() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/integrations/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  if (!status) return null;
  const fmtDT = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
  };
  const vf = status.verifactu || {};
  const noah = status.noah || [];
  const bk = status.backup || {};
  const vfOk = vf.enabled && vf.configured && vf.has_certificate;
  const certExpired = vf.has_certificate && vf.cert_days_left != null && vf.cert_days_left < 0;
  const certExpiring = vf.has_certificate && vf.cert_days_left != null && vf.cert_days_left >= 0 && vf.cert_days_left <= 60;
  const certDate = vf.cert_valid_to ? fmtDate_(vf.cert_valid_to) : "";
  function fmtDate_(iso) {
    try { return new Date(iso).toLocaleDateString("es-ES", { dateStyle: "medium" }); } catch { return iso; }
  }
  const StatusBadge = ({ ok, warn, danger, children }) => (
    <Badge className={danger ? "bg-[#E8D1D1] text-[#8A2E2E] hover:bg-[#E8D1D1]" : ok ? "bg-[#D1E0D7] text-[#2A2F2C] hover:bg-[#D1E0D7]" : warn ? "bg-[#FBF3E4] text-[#7C5A2A] hover:bg-[#FBF3E4]" : "bg-[#EAEAEA] text-[#6E7772] hover:bg-[#EAEAEA]"}>
      {children}
    </Badge>
  );
  const Row = ({ title, badge, detail, testid }) => (
    <div className="flex items-start justify-between border-b border-[#E5E3DB] last:border-0 py-3 gap-3" data-testid={testid}>
      <div>
        <div className="text-sm font-medium text-[#2A2F2C]">{title}</div>
        <div className="text-xs text-[#6E7772] mt-0.5">{detail}</div>
      </div>
      {badge}
    </div>
  );
  return (
    <Card className="border-[#E5E3DB] shadow-none bg-white" data-testid="integrations-status-card">
      <CardHeader>
        <CardTitle className="font-heading font-medium text-lg flex items-center gap-2">
          <Plug className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} /> Estado de integraciones
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Row
          testid="integration-verifactu"
          title="Veri*Factu (AEAT)"
          badge={
            certExpired ? <StatusBadge danger>Certificado caducado</StatusBadge>
              : certExpiring ? <StatusBadge warn>{`Certificado caduca en ${vf.cert_days_left} día${vf.cert_days_left === 1 ? "" : "s"}`}</StatusBadge>
                : vfOk ? <StatusBadge ok>{vf.production ? "Activo · Producción" : "Activo · Pruebas"}</StatusBadge>
                  : vf.enabled ? <StatusBadge warn>Incompleto</StatusBadge>
                    : <StatusBadge>Desactivado</StatusBadge>
          }
          detail={
            certExpired ? `⚠️ El certificado digital caducó el ${certDate}. Las facturas no se pueden firmar ni enviar a la AEAT: renueva tu certificado y súbelo en Configuración → Veri*Factu.`
              : certExpiring ? `⚠️ El certificado digital caduca el ${certDate}. Renueva tu certificado FNMT con antelación y súbelo en Configuración → Veri*Factu.`
                : !vf.enabled ? "Actívalo en Configuración → Veri*Factu."
                  : !vf.configured ? "Falta el NIF emisor o la razón social."
                    : !vf.has_certificate ? "Falta subir el certificado digital (.p12)."
                      : `${vf.accepted} aceptadas · ${vf.pending} pendientes de envío${vf.rejected ? ` · ${vf.rejected} con error` : ""}${vf.last_sent_at ? ` · Último envío ${fmtDT(vf.last_sent_at)}` : ""}`
          }
        />
        {noah.length === 0 ? (
          <Row
            testid="integration-noah"
            title="Noah ES"
            badge={<StatusBadge>Desactivado</StatusBadge>}
            detail="Sin configurar. Actívalo en Configuración → Noah ES."
          />
        ) : (
          noah.map((n) => (
            <Row
              key={n.center_id}
              testid={`integration-noah-${n.center_id}`}
              title={`Noah ES · ${n.center_name || "Global"}`}
              badge={
                n.enabled && n.configured ? <StatusBadge ok>{n.auto_sync_enabled ? "Activo · Auto" : "Activo"}</StatusBadge>
                  : n.enabled ? <StatusBadge warn>Incompleto</StatusBadge>
                    : <StatusBadge>Desactivado</StatusBadge>
              }
              detail={
                n.enabled && n.configured
                  ? (n.last_sync_at ? `Última sincronización ${fmtDT(n.last_sync_at)}` : "Aún sin sincronizaciones")
                  : n.enabled ? "Faltan credenciales (tenant / API key)." : "Desactivado para este centro."
              }
            />
          ))
        )}
        <Row
          testid="integration-backup"
          title="Copias de seguridad"
          badge={bk.last_backup_at ? <StatusBadge ok>Activo</StatusBadge> : <StatusBadge warn>Sin copias</StatusBadge>}
          detail={`${bk.last_backup_at ? `Última copia ${fmtDT(bk.last_backup_at)}` : "Todavía no hay copias"}${bk.email_enabled ? (bk.email_ready ? ` · Email diario a ${(bk.email_recipients || []).join(", ")}` : " · Email pendiente de la clave Resend") : ""}`}
        />
      </CardContent>
    </Card>
  );
}
